import { Loader, Select } from "@/components";
import { Badge, Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ResetPassword } from "@/pages";
import {
  useActiveSessions, useGetCommunities, useLoginActivity, usePasswordResetHistory, useRevokeSessionMutation,
  useUserAccounts, useUserManagementSummary,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity, AlertTriangle, ChevronLeft, ChevronRight, Clock, KeyRound, Lock, Search, ShieldCheck, UserCheck, Users,
} from "lucide-react";
import moment from "moment";
import { ReactNode, useEffect, useState } from "react";
import { toast } from "@/components/Toast";

const ROLE_LABELS: Record<string, string> = {
  ROLE_ADMIN: "Admin", ROLE_CAM: "CAM", ROLE_CAM_ACCOUNTANT: "CAM-A", ROLE_OWNER: "Owner", ROLE_TENANT: "Tenant",
  ROLE_BOARD_MEMBER: "Board Member", ROLE_PROPERTY_MANAGER: "Property Manager", ROLE_EMPLOYEE: "Employee", ROLE_USER: "User",
};
const roles = (csv?: string) => (csv ? csv.split(",").map((r) => ROLE_LABELS[r] || r.replace(/^ROLE_/, "")).join(", ") : "—");
const name = (r: any) => [r?.firstName, r?.lastName].filter(Boolean).join(" ") || r?.userName || "—";
const dt = (v?: string) => (v ? moment(v).format("MM/DD/YYYY, h:mm:ss A") : "—");

type Tab = "overview" | "accounts" | "activity" | "sessions" | "reset";
const TABS: { key: Tab; label: string; icon: ReactNode; disabled?: boolean }[] = [
  { key: "overview", label: "Overview", icon: <Activity className="w-4 h-4" /> },
  { key: "accounts", label: "User Accounts", icon: <Users className="w-4 h-4" /> },
  { key: "activity", label: "Login Activity", icon: <Clock className="w-4 h-4" /> },
  { key: "sessions", label: "Active Sessions", icon: <ShieldCheck className="w-4 h-4" /> },
  { key: "reset", label: "Password Reset", icon: <KeyRound className="w-4 h-4" /> },
];

const Card = ({ label, value, icon }: { label: string; value: ReactNode; icon: ReactNode }) => (
  <div className="bg-white rounded-xl shadow-sm p-5 flex items-start justify-between">
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value ?? "—"}</p>
    </div>
    {icon}
  </div>
);

const Status = ({ active, labels = ["ACTIVE", "INACTIVE"] }: { active: boolean; labels?: [string, string] }) => (
  <Badge variant="outline" className={cn("rounded-md px-2 py-0.5 text-xs",
    active ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-600")}>
    {active ? labels[0] : labels[1]}
  </Badge>
);

/** Table with a header row, empty state and simple paging footer. */
const PagedTable = ({ headers, rows, total, page, setPage, size, setSize, isLoading, empty }: {
  headers: string[]; rows: ReactNode[][]; total: number; page: number; setPage: (p: number) => void;
  size: number; setSize: (s: number) => void; isLoading: boolean; empty: string;
}) => {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <div className="flex flex-col gap-2">
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>{headers.map((h) => <TableHead key={h} className="font-semibold text-gray-700 whitespace-nowrap">{h}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={headers.length}><Loader /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={headers.length} className="text-center text-gray-500 h-16">{empty}</TableCell></TableRow>
            ) : rows.map((cells, i) => (
              <TableRow key={i}>{cells.map((c, j) => <TableCell key={j}>{c}</TableCell>)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end gap-4 text-sm text-gray-600">
        <span>Rows per page:</span>
        <select className="bg-transparent" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}>
          {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>{total === 0 ? 0 : page * size + 1} - {Math.min(total, (page + 1) * size)} of {total}</span>
        <button disabled={page === 0} onClick={() => setPage(page - 1)} className="disabled:opacity-30" aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></button>
        <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="disabled:opacity-30" aria-label="Next page"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const usePaging = (deps: any[]) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  useEffect(() => setPage(0), deps); // eslint-disable-line react-hooks/exhaustive-deps
  return { page, setPage, size, setSize };
};

/** Admin Console > User Management (admin only). */
const UserManagement = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [communityId, setCommunityId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const { data: communityList } = useGetCommunities({});
  const communityOptions = [{ value: "all", label: "All Communities" },
    ...(communityList?.data || []).map((c: any) => ({ value: String(c.communityId), label: c.name }))];
  const cid = communityId && communityId !== "all" ? communityId : undefined;

  const { data: summary } = useUserManagementSummary({ communityId: cid });

  // ---- User Accounts
  const acc = usePaging([cid, search]);
  const { data: accounts, isLoading: accLoading } = useUserAccounts({ communityId: cid, search, page: acc.page, size: acc.size });

  // ---- Login Activity
  const [userFilter, setUserFilter] = useState("all");
  const [result, setResult] = useState("ALL");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const act = usePaging([cid, search, userFilter, result, from, to]);
  const { data: userOptionsData } = useUserAccounts({ communityId: cid, page: 0, size: 500 });
  const { data: activity, isLoading: actLoading } = useLoginActivity({
    communityId: cid, search, userId: userFilter === "all" ? undefined : userFilter,
    result: result === "ALL" ? undefined : result, from, to, page: act.page, size: act.size,
  });

  // ---- Active Sessions
  const ses = usePaging([cid, search]);
  const { data: sessions, isLoading: sesLoading } = useActiveSessions({ communityId: cid, search, page: ses.page, size: ses.size });
  const { mutate: revoke, isPending: isRevoking } = useRevokeSessionMutation({
    onError: () => toast.error("Could not revoke the session"),
    onSuccess: () => {
      toast.success("Session revoked — that login is signed out");
      queryClient.invalidateQueries({ queryKey: ["user-management"] });
    },
  });

  // ---- Password Reset
  const rst = usePaging([cid, search]);
  const hist = usePaging([cid, search]);
  const { data: resetUsers, isLoading: rstLoading } = useUserAccounts({ communityId: cid, search, page: rst.page, size: rst.size });
  const { data: history, isLoading: histLoading } = usePasswordResetHistory({ communityId: cid, search, page: hist.page, size: hist.size });
  const [resetUser, setResetUser] = useState<any>(null);
  const [openReset, setOpenReset] = useState<any>(false);
  useEffect(() => { if (!openReset) queryClient.invalidateQueries({ queryKey: ["user-management", "password-resets"] }); }, [openReset]);

  return (
    <div className="p-4 md:p-6 flex flex-col gap-5">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-semibold">User Management</h2>
          <p className="text-sm text-gray-500">Authentication activity, account visibility, sessions, and Admin password reset.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-60">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Community</p>
            <Select key={communityOptions.length} options={communityOptions} value={communityId || "all"}
              onChange={(v: any) => setCommunityId(String(v))} className="h-[40px] w-full" />
          </div>
          <div className="w-full sm:w-72">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">User ID / Login ID</p>
            <div className="flex gap-1">
              <Input className="bg-white h-[40px]" placeholder="Press Enter to search" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput.trim()); }} />
              <Button type="button" size="icon" className="h-[40px] w-[40px]" onClick={() => setSearch(searchInput.trim())} aria-label="Search">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Total Users" value={summary?.totalUsers} icon={<Users className="w-6 h-6 text-primary" />} />
        <Card label="Active Users" value={summary?.activeUsers} icon={<UserCheck className="w-6 h-6 text-green-600" />} />
        <Card label="Active Sessions" value={summary?.activeSessions} icon={<ShieldCheck className="w-6 h-6 text-purple-600" />} />
        <Card label="Failed Today" value={summary?.failedToday} icon={<AlertTriangle className="w-6 h-6 text-orange-500" />} />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1">
        {TABS.map((t) => (
          <button key={t.key} type="button" disabled={t.disabled} title={t.disabled ? "Next phase" : undefined}
            onClick={() => setTab(t.key)}
            className={cn("flex items-center justify-center gap-2 h-10 rounded-lg text-sm",
              tab === t.key ? "bg-gray-100 font-medium" : "text-gray-600 hover:bg-gray-50",
              t.disabled && "opacity-40 cursor-not-allowed hover:bg-transparent")}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col gap-4">
        {tab === "overview" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border rounded-xl p-4 flex flex-col gap-3">
              <div>
                <h3 className="font-semibold">Today's authentication</h3>
                <p className="text-sm text-gray-500">All values reflect the selected community.</p>
              </div>
              {[
                ["Successful logins", summary?.successToday],
                ["Failed attempts", summary?.failedToday],
                ["Password resets", summary?.passwordResetsToday],
                ["Locked accounts", summary?.lockedAccounts],
              ].map(([label, value]) => (
                <div key={label as string} className="flex items-center justify-between border-b last:border-b-0 py-2 text-sm">
                  <span className="text-gray-600">{label}</span>
                  <span className="font-semibold">{(value as any) ?? "—"}</span>
                </div>
              ))}
            </div>
            <div className="border rounded-xl p-4 flex flex-col gap-3">
              <div>
                <h3 className="font-semibold">Administrative focus</h3>
                <p className="text-sm text-gray-500">Operational items requiring attention.</p>
              </div>
              <button type="button" className="border rounded-lg p-3 flex gap-3 text-left hover:bg-gray-50"
                onClick={() => { setResult("FAILURE"); setTab("activity"); }}>
                <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                <span>
                  <span className="block text-sm font-medium">Review failed attempts</span>
                  <span className="block text-xs text-gray-500">
                    {summary?.failedToday ?? 0} failed today. Investigate repeated failures by account, IP or browser.
                  </span>
                </span>
              </button>
              <div className="border rounded-lg p-3 flex gap-3">
                <Lock className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" />
                <span>
                  <span className="block text-sm font-medium">Locked accounts</span>
                  <span className="block text-xs text-gray-500">
                    {summary?.lockedAccounts ? `${summary.lockedAccounts} account(s) currently require review.` : "No locked accounts."}
                  </span>
                </span>
              </div>
              <div className="border rounded-lg p-3 flex gap-3">
                <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span>
                  <span className="block text-sm font-medium">Session integrity</span>
                  <span className="block text-xs text-gray-500">
                    Explicit logout and password resets now terminate tracked sessions. Admins can revoke any session.
                  </span>
                </span>
              </div>
            </div>
          </div>
        ) : tab === "accounts" ? (
          <>
            <div>
              <h3 className="font-semibold">User Accounts</h3>
              <p className="text-sm text-gray-500">Includes Admin, CAM, CAM-A, Owner and Tenant accounts. Each role is documented against its User ID.</p>
            </div>
            <PagedTable {...acc} total={accounts?.total || 0} isLoading={accLoading} empty="No users found."
              headers={["User ID", "Person", "Login ID", "Communities", "User Role", "Last Login", "Sessions", "Status"]}
              rows={(accounts?.data || []).map((u: any) => [
                <span className="text-primary font-semibold">{u.userId}</span>,
                <div><p className="font-medium">{name(u)}</p><p className="text-xs text-gray-500">{u.email || "—"}</p></div>,
                u.loginId, u.communities || "—", roles(u.roles), dt(u.lastLogin), u.sessions, <Status active={u.active} />,
              ])} />
          </>
        ) : tab === "activity" ? (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-56">
                <Select key={`u-${userOptionsData?.data?.length || 0}`} value={userFilter} onChange={(v: any) => setUserFilter(String(v))}
                  className="h-[38px] w-full"
                  options={[{ value: "all", label: "All Users" },
                    ...(userOptionsData?.data || []).map((u: any) => ({ value: String(u.userId), label: `${u.userId} · ${name(u)}` }))]} />
              </div>
              <div className="w-40">
                <Select value={result} onChange={(v: any) => setResult(String(v))} className="h-[38px] w-full"
                  options={[{ value: "ALL", label: "All Results" }, { value: "SUCCESS", label: "Success" }, { value: "FAILURE", label: "Failure" }]} />
              </div>
              <label className="text-xs text-gray-500 flex flex-col">From
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded-md h-[38px] px-2 text-sm" />
              </label>
              <label className="text-xs text-gray-500 flex flex-col">To
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded-md h-[38px] px-2 text-sm" />
              </label>
            </div>
            <PagedTable {...act} total={activity?.total || 0} isLoading={actLoading} empty="No login activity recorded yet."
              headers={["Date & Time", "User", "User Role", "Result", "Reason", "IP / Country", "Browser / OS", "Device"]}
              rows={(activity?.data || []).map((e: any) => [
                dt(e.createdAt),
                <div><p className="font-medium">{e.userId ? `${e.userId} · ${name(e)}` : "Unknown user"}</p><p className="text-xs text-gray-500">{e.loginIdentifier}</p></div>,
                roles(e.roles), <Status active={e.success} labels={["SUCCESS", "FAILURE"]} />, e.reason || "—",
                <div><p>{e.ipAddress || "—"}</p><p className="text-xs text-gray-500">Unknown</p></div>,
                `${e.browser || "—"} / ${e.os || "—"}`, e.device || "—",
              ])} />
          </>
        ) : tab === "sessions" ? (
          <>
            <div>
              <h3 className="font-semibold">Active Sessions</h3>
              <p className="text-sm text-gray-500">Signed-in logins that haven't expired. Revoking signs that login out immediately.</p>
            </div>
            <PagedTable {...ses} total={sessions?.total || 0} isLoading={sesLoading} empty="No active sessions."
              headers={["User", "User Role", "Signed In", "Last Seen", "Expires", "IP / Browser", "Device", ""]}
              rows={(sessions?.data || []).map((s: any) => [
                <div><p className="font-medium">{s.userId} · {name(s)}</p><p className="text-xs text-gray-500">{s.loginId}</p></div>,
                roles(s.roles), dt(s.issuedAt), dt(s.lastSeenAt), dt(s.expiresAt),
                <div><p>{s.ipAddress || "—"}</p><p className="text-xs text-gray-500">{s.browser} / {s.os}</p></div>,
                s.device || "—",
                <Button type="button" size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                  disabled={isRevoking} onClick={() => revoke({ sessionId: s.sessionId, reason: "Revoked by admin" })}>Revoke</Button>,
              ])} />
          </>
        ) : tab === "reset" ? (
          <>
            <div>
              <h3 className="font-semibold">Password Reset</h3>
              <p className="text-sm text-gray-500">Find the user (search above) and set a new password. The user must change it at next login.</p>
            </div>
            <PagedTable {...rst} total={resetUsers?.total || 0} isLoading={rstLoading} empty="No users found."
              headers={["User ID", "Person", "Login ID", "User Role", "Status", ""]}
              rows={(resetUsers?.data || []).map((u: any) => [
                u.userId, name(u), u.loginId, roles(u.roles), <Status active={u.active} />,
                <Button type="button" size="sm" variant="outline" disabled={!u.active}
                  title={u.active ? undefined : "Activate the account first"}
                  onClick={() => { setResetUser({ userId: u.userId }); setOpenReset(true); }}>
                  <KeyRound className="w-4 h-4" /> Reset Password
                </Button>,
              ])} />
            <h4 className="font-semibold mt-2">Reset history</h4>
            <PagedTable {...hist} total={history?.total || 0} isLoading={histLoading} empty="No admin password resets recorded yet."
              headers={["Date & Time", "User", "Login ID", "Reset By", "Method"]}
              rows={(history?.data || []).map((h: any) => [
                dt(h.createdAt), `${h.userId} · ${name(h)}`, h.loginId, h.resetByName || "—", h.method === "ADMIN_RESET" ? "Admin reset" : h.method,
              ])} />
            <ResetPassword open={openReset} setOpen={setOpenReset} selectedUser={resetUser} />
          </>
        ) : <></>}
      </div>
    </div>
  );
};

export default UserManagement;
